import { Terminal } from 'lucide-react';
import { useEffect } from 'react';
import { cn } from '../lib/cn';

export type SlashCommand = { cmd: string; desc: string; prompt: string };

/** Quick prompts replace Claude-only CLI commands in the workspace. */
export const CLI_SLASH_COMMANDS: SlashCommand[] = [
  {
    cmd: 'review',
    desc: 'Найти ошибки и регрессии',
    prompt: 'Проверь последние изменения на ошибки и регрессии. Файлы не меняй.',
  },
  {
    cmd: 'security',
    desc: 'Проверить безопасность',
    prompt: 'Проверь проект на риски безопасности. Файлы не меняй.',
  },
  {
    cmd: 'tests',
    desc: 'Запустить нужные тесты',
    prompt: 'Запусти тесты, относящиеся к текущей работе, и сообщи о сбоях.',
  },
  {
    cmd: 'explain',
    desc: 'Объяснить структуру проекта',
    prompt: 'Объясни структуру этого проекта и основные компоненты.',
  },
];

type Props = {
  query: string;
  activeIndex: number;
  onPick: (cmd: SlashCommand) => void;
  onMove: (delta: number) => void;
  onClose: () => void;
};

/**
 * Dropdown above the composer that appears when the input starts
 * with `/`. Arrow keys / Enter / Tab selection is handled by the
 * composer (it owns focus); this component is presentational.
 */
export function SlashCommandsPopover({ query, activeIndex, onPick, onMove, onClose }: Props) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const matches = filterCommands(query);
  if (matches.length === 0) return null;

  return (
    <div className="mc-menu-enter origin-bottom absolute bottom-[calc(100%+8px)] left-3 right-3 z-20 max-h-[260px] overflow-y-auto rounded-2xl border border-border/70 bg-surface p-1 mc-float">
      {matches.map((c, i) => (
        <button
          key={c.cmd}
          type="button"
          onMouseEnter={() => onMove(i - activeIndex)}
          onMouseDown={(e) => {
            // mousedown fires before textarea blur — keeps focus.
            e.preventDefault();
          }}
          onClick={() => onPick(c)}
          className={cn(
            'flex w-full items-start gap-2 rounded-md px-2.5 py-1.5 text-left',
            i === activeIndex ? 'bg-subtle' : 'hover:bg-subtle',
          )}
        >
          <Terminal size={12} className="mt-1 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[13px]">/{c.cmd}</div>
            <div className="truncate text-[11.5px] text-muted-foreground">{c.desc}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

/** Autocomplete ends once the user starts arguments; their text is then a message. */
export function slashQueryForText(text: string): string | null {
  return text.startsWith('/') && !/\s/.test(text) ? text.slice(1) : null;
}

export function filterCommands(query: string): SlashCommand[] {
  const q = query.trim().toLowerCase();
  if (!q) return CLI_SLASH_COMMANDS;
  return CLI_SLASH_COMMANDS.filter((c) => c.cmd.includes(q) || c.desc.toLowerCase().includes(q));
}
